import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Moon, Target, Sparkles, ChevronRight, Loader2, ArrowLeft,
  CheckCircle2, CircleDot, XCircle, Flame, TrendingUp, Star,
  Compass, Zap, Shield
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { DictationButton } from '../components/DictationButton';

const WIN_OPTIONS = [
  { id: 'yes', icon: CheckCircle2, en: 'Yes', tr: 'Evet', color: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300' },
  { id: 'partly', icon: CircleDot, en: 'Partly', tr: 'Kısmen', color: 'bg-amber-500/20 border-amber-500/40 text-amber-300' },
  { id: 'no', icon: XCircle, en: 'No', tr: 'Hayır', color: 'bg-red-500/20 border-red-500/40 text-red-300' },
];

export const EveningCheckIn = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  // 0=check, 1=win check, 2=wins, 3=improve, 4=generating, 5=result, 6=milestone
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [morningOneWin, setMorningOneWin] = useState('');
  const [winAchieved, setWinAchieved] = useState('');
  const [winReflection, setWinReflection] = useState('');
  const [wins, setWins] = useState('');
  const [improve, setImprove] = useState('');

  const [output, setOutput] = useState(null);
  const [streak, setStreak] = useState(0);
  const [milestone, setMilestone] = useState(null);
  const [isMilestone, setIsMilestone] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    checkExisting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkExisting = async () => {
    try {
      const response = await api.get('/checkin/evening/today');
      if (response.data.submitted) {
        setOutput(response.data.output);
        setStreak(response.data.streak || 0);
        setMorningOneWin(response.data.morning_one_win || '');
        setMilestone(response.data.milestone);
        setIsMilestone(response.data.is_milestone || false);
        // Show milestone first if exists, otherwise output
        setStep(response.data.milestone ? 6 : 5);
      } else {
        setMorningOneWin(response.data.morning_one_win || '');
        setStep(response.data.morning_one_win ? 1 : 2);
      }
    } catch {
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const submitEvening = async () => {
    setGenerating(true);
    setStep(4);
    try {
      const response = await api.post('/checkin/evening', {
        win_achieved: winAchieved || 'no',
        win_reflection: winReflection,
        wins,
        improve,
      });
      setOutput(response.data.output);
      setStreak(response.data.streak || 0);
      setMorningOneWin(response.data.morning_one_win || morningOneWin);
      setMilestone(response.data.milestone);
      setIsMilestone(response.data.is_milestone || false);
      // Show milestone screen first if it's a milestone
      setStep(response.data.milestone ? 6 : 5);
    } catch (error) {
      console.error('Evening check-in error:', error);
      toast.error(language === 'tr' ? 'Akşam kontrolü kaydedilemedi' : 'Failed to save check-in');
      setStep(3);
    } finally {
      setGenerating(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return winAchieved !== '';
    if (step === 2) return wins.trim().length > 0;
    if (step === 3) return improve.trim().length > 0;
    return false;
  };

  const handleNext = () => {
    if (step === 1 && !morningOneWin) setStep(2);
    else if (step < 3) setStep(step + 1);
    else submitEvening();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Moon className="w-12 h-12 text-[#D4AF37] animate-pulse" />
      </div>
    );
  }

  // MILESTONE SCREEN
  if (step === 6 && milestone) {
    return (
      <MilestoneScreen
        milestone={milestone}
        streak={streak}
        language={language}
        onContinue={() => setStep(5)}
      />
    );
  }

  // RESULT SCREEN
  if (step === 5 && output) {
    return <EveningOutput output={output} streak={streak} morningOneWin={morningOneWin} language={language} navigate={navigate} />;
  }

  // GENERATING
  if (step === 4) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-6" />
          <h2 className="font-serif text-2xl text-[#F8F5F2] mb-2">
            {language === 'tr' ? 'Günü kapatıyoruz...' : 'Closing the loop...'}
          </h2>
          <p className="text-[#6B7280] font-sans text-sm">
            {language === 'tr' ? 'Günlük döngünüz tamamlanıyor' : 'Completing your daily cycle'}
          </p>
        </motion.div>
      </div>
    );
  }

  // INPUT FLOW
  const totalSteps = morningOneWin ? 3 : 2;
  const stepOffset = morningOneWin ? 0 : 1;

  return (
    <div className="min-h-screen relative py-12 px-6">
      <div className="max-w-lg mx-auto">
        <button
          onClick={() => {
            if (step === 1 || (step === 2 && !morningOneWin)) navigate('/dashboard');
            else setStep(step - 1);
          }}
          className="text-white/50 hover:text-white transition-colors mb-6 flex items-center gap-2 text-sm font-sans uppercase tracking-widest"
          data-testid="evening-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {(step === 1 || (step === 2 && !morningOneWin)) ? (language === 'tr' ? 'Panel' : 'Dashboard') : (language === 'tr' ? 'Geri' : 'Back')}
        </button>

        <div className="flex items-center justify-center gap-2 mb-10">
          {Array.from({ length: totalSteps }, (_, i) => i + 1 + stepOffset).map((s) => (
            <div
              key={s}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                s === step ? 'w-8 bg-[#D4AF37]' : s < step ? 'w-4 bg-[#D4AF37]/50' : 'w-4 bg-white/10'
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 1 && morningOneWin && (
            <motion.div key="estep1" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="space-y-8">
              <div className="text-center">
                <Target className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h2 className="font-serif text-2xl text-[#F8F5F2] mb-1">
                  {language === 'tr' ? 'Döngüyü Kapat' : 'Close the Loop'}
                </h2>
                <p className="text-[#6B7280] font-sans text-sm">
                  {language === 'tr' ? 'Bu sabah bir hedef belirlediniz' : 'You set a target this morning'}
                </p>
              </div>

              <div className="p-4 rounded-xl bg-[#D4AF37]/5 border border-[#D4AF37]/20" data-testid="morning-win-reference">
                <p className="text-xs uppercase tracking-wider text-[#D4AF37] font-sans mb-2">
                  {language === 'tr' ? 'Bugünkü Hedef' : "Today's One Win"}
                </p>
                <p className="text-[#F8F5F2] font-serif text-lg italic">"{morningOneWin}"</p>
              </div>

              <div data-testid="win-achieved-section">
                <label className="text-xs uppercase tracking-wider text-[#A1A1AA] font-sans block mb-3">
                  {language === 'tr' ? 'Başardınız mı?' : 'Did you achieve it?'}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {WIN_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setWinAchieved(opt.id)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border text-sm font-sans transition-all duration-200 ${
                          winAchieved === opt.id ? opt.color + ' ring-1 ring-white/20 scale-105' : 'bg-white/5 border-white/10 text-[#A1A1AA] hover:bg-white/10'
                        }`}
                        data-testid={`win-${opt.id}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span>{language === 'tr' ? opt.tr : opt.en}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {winAchieved && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <label className="text-xs uppercase tracking-wider text-[#A1A1AA] font-sans block mb-2">
                    {winAchieved === 'yes' ? (language === 'tr' ? 'Nasıl hissettirdi?' : 'How did it feel?')
                      : winAchieved === 'partly' ? (language === 'tr' ? 'Ne oldu?' : 'What happened?')
                      : (language === 'tr' ? 'Ne engel oldu?' : 'What got in the way?')}
                  </label>
                  <div className="relative">
                    <input type="text" value={winReflection} onChange={(e) => setWinReflection(e.target.value)}
                      placeholder={language === 'tr' ? 'Kısa yansıma... (isteğe bağlı)' : 'Brief reflection... (optional)'}
                      className="w-full bg-white/5 border border-white/10 rounded-xl p-3 pr-12 text-[#F8F5F2] placeholder-[#4B5563] font-sans text-sm focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                      data-testid="win-reflection-input" />
                    <div className="absolute top-2 right-2">
                      <DictationButton onTranscript={(text) => setWinReflection(prev => prev ? prev + ' ' + text : text)} />
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key="estep2" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="space-y-6">
              <div className="text-center">
                <Star className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h2 className="font-serif text-2xl text-[#F8F5F2] mb-1">{language === 'tr' ? 'Günün Kazanımları' : 'Wins & Gains'}</h2>
                <p className="text-[#6B7280] font-sans text-sm">{language === 'tr' ? 'Bugün ne iyi gitti?' : 'What went well today?'}</p>
              </div>
              <div className="relative" data-testid="wins-section">
                <textarea value={wins} onChange={(e) => setWins(e.target.value)}
                  placeholder={language === 'tr' ? 'Kazanımlarınızı, ilerlemelerinizi yazın...' : 'Your wins, progress, things that went well...'}
                  rows={4} className="w-full bg-white/5 border border-white/10 rounded-xl p-4 pr-12 text-[#F8F5F2] placeholder-[#4B5563] font-sans text-sm resize-none focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                  data-testid="wins-input" />
                <div className="absolute top-3 right-3">
                  <DictationButton onTranscript={(text) => setWins(prev => prev ? prev + ' ' + text : text)} />
                </div>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key="estep3" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} className="space-y-6">
              <div className="text-center">
                <TrendingUp className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
                <h2 className="font-serif text-2xl text-[#F8F5F2] mb-1">{language === 'tr' ? 'Yarın İçin' : 'For Tomorrow'}</h2>
                <p className="text-[#6B7280] font-sans text-sm">{language === 'tr' ? 'Neyi farklı yapardınız?' : 'What would you do differently?'}</p>
              </div>
              <div className="relative" data-testid="improve-section">
                <textarea value={improve} onChange={(e) => setImprove(e.target.value)}
                  placeholder={language === 'tr' ? 'Yarın odaklanmak istediğiniz...' : 'What you want to focus on tomorrow...'}
                  rows={4} className="w-full bg-white/5 border border-white/10 rounded-xl p-4 pr-12 text-[#F8F5F2] placeholder-[#4B5563] font-sans text-sm resize-none focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
                  data-testid="improve-input" />
                <div className="absolute top-3 right-3">
                  <DictationButton onTranscript={(text) => setImprove(prev => prev ? prev + ' ' + text : text)} />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {step >= 1 && step <= 3 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-10">
            <button onClick={handleNext} disabled={!canProceed()}
              className="w-full py-4 rounded-2xl font-sans text-sm uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed bg-[#D4AF37] text-[#030305] hover:bg-[#FFBF00]"
              data-testid="evening-next-btn">
              {step === 3 ? (language === 'tr' ? 'Günü Kapat' : 'Close the Day') : (language === 'tr' ? 'Devam' : 'Continue')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );
};


// ==================== MILESTONE SCREEN ====================

const MilestoneScreen = ({ milestone, streak, language, onContinue }) => {
  return (
    <div className="min-h-screen relative py-10 px-6">
      <div className="max-w-lg mx-auto">
        {/* Solar Streak Hero */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-amber-500/20 via-orange-500/25 to-amber-500/20 border-2 border-amber-400/60 shadow-[0_0_30px_rgba(245,158,11,0.4)] animate-pulse mb-4">
            <Flame className="w-7 h-7 text-orange-300" />
            <span className="text-4xl font-serif text-amber-200">{streak}</span>
            <Flame className="w-7 h-7 text-orange-300" />
          </div>
          <p className="text-xs tracking-[0.3em] uppercase text-amber-400 font-sans mb-4">
            {language === 'tr' ? 'SOLAR SERİ' : 'SOLAR STREAK'}
          </p>
        </motion.div>

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center mb-8"
        >
          <p className="font-serif text-xl text-[#F8F5F2] leading-relaxed italic">
            "{milestone.headline}"
          </p>
        </motion.div>

        {/* Pattern Summary */}
        {milestone.pattern_summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-panel rounded-2xl p-5 mb-4"
            data-testid="milestone-pattern"
          >
            <div className="flex items-center gap-2 mb-3">
              <Compass className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-sans">
                {language === 'tr' ? 'Kalıp Özeti' : 'Pattern Summary'}
              </span>
            </div>
            <p className="text-[#D1D1D6] font-sans text-sm leading-relaxed">{milestone.pattern_summary}</p>
          </motion.div>
        )}

        {/* Strongest Domain + Friction Point */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {milestone.strongest_domain && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="rounded-2xl p-4 bg-emerald-500/5 border border-emerald-500/20"
              data-testid="milestone-strongest"
            >
              <div className="flex items-center gap-1.5 mb-2">
                <Zap className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[10px] uppercase tracking-wider text-emerald-400 font-sans">
                  {language === 'tr' ? 'Güçlü Alan' : 'Strongest'}
                </span>
              </div>
              <p className="text-[#F8F5F2] font-sans text-sm font-medium capitalize">{milestone.strongest_domain.name}</p>
              <p className="text-emerald-300 font-serif text-lg">{milestone.strongest_domain.score}/10</p>
              <p className="text-[#A1A1AA] font-sans text-xs mt-1 leading-relaxed">{milestone.strongest_domain.insight}</p>
            </motion.div>
          )}

          {milestone.friction_point && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="rounded-2xl p-4 bg-amber-500/5 border border-amber-500/20"
              data-testid="milestone-friction"
            >
              <div className="flex items-center gap-1.5 mb-2">
                <Shield className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-[10px] uppercase tracking-wider text-amber-400 font-sans">
                  {language === 'tr' ? 'Sürtünme' : 'Friction'}
                </span>
              </div>
              <p className="text-[#F8F5F2] font-sans text-sm font-medium capitalize">{milestone.friction_point.name}</p>
              <p className="text-amber-300 font-serif text-lg">{milestone.friction_point.score}/10</p>
              <p className="text-[#A1A1AA] font-sans text-xs mt-1 leading-relaxed">{milestone.friction_point.insight}</p>
            </motion.div>
          )}
        </div>

        {/* Solar Alignment */}
        {milestone.solar_alignment && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="rounded-2xl p-5 mb-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20 text-center"
            data-testid="milestone-solar"
          >
            <p className="text-xs uppercase tracking-wider text-[#A1A1AA] font-sans mb-2">
              {language === 'tr' ? 'Güneş Uyumu' : 'Solar Alignment'}
            </p>
            <p className="text-4xl font-serif text-[#D4AF37] mb-2">{milestone.solar_alignment}</p>
            <p className="text-[#D1D1D6] font-sans text-sm leading-relaxed">{milestone.solar_insight}</p>
          </motion.div>
        )}

        {/* Win Stats */}
        {milestone.win_stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex justify-center gap-6 mb-6"
          >
            <div className="text-center">
              <p className="text-lg font-serif text-emerald-400">{milestone.win_stats.wins}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-sans">{language === 'tr' ? 'Kazanım' : 'Wins'}</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-serif text-amber-400">{milestone.win_stats.partials}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-sans">{language === 'tr' ? 'Kısmi' : 'Partial'}</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-serif text-[#A1A1AA]">{milestone.win_stats.total}</p>
              <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-sans">{language === 'tr' ? 'Toplam' : 'Total'}</p>
            </div>
          </motion.div>
        )}

        {/* Closing */}
        {milestone.closing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
            className="text-center mb-8"
          >
            <Sparkles className="w-5 h-5 text-[#D4AF37] mx-auto mb-3" />
            <p className="text-[#F8F5F2] font-serif text-lg italic">"{milestone.closing}"</p>
          </motion.div>
        )}

        {/* Continue to Evening Output */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1 }}
        >
          <button
            onClick={onContinue}
            className="w-full py-4 rounded-2xl font-sans text-sm uppercase tracking-widest transition-all duration-300 bg-[#D4AF37] text-[#030305] hover:bg-[#FFBF00] flex items-center justify-center gap-2"
            data-testid="milestone-continue-btn"
          >
            {language === 'tr' ? 'Akşam Kapanışına Devam Et' : 'Continue to Evening Closing'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};


// ==================== EVENING OUTPUT SCREEN ====================

const EveningOutput = ({ output, streak, morningOneWin, language, navigate }) => {
  return (
    <div className="min-h-screen relative py-12 px-6">
      <div className="max-w-lg mx-auto">
        <button onClick={() => navigate('/dashboard')}
          className="text-white/50 hover:text-white transition-colors mb-8 flex items-center gap-2 text-sm font-sans uppercase tracking-widest"
          data-testid="output-back-btn">
          <ArrowLeft className="w-4 h-4" />
          {language === 'tr' ? 'Panel' : 'Dashboard'}
        </button>

        {/* Solar Streak Badge */}
        {streak > 0 && (
          <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} className="text-center mb-8">
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30">
              <Flame className="w-5 h-5 text-orange-400" />
              <span className="text-amber-300 font-sans text-sm font-semibold tracking-wide">
                {streak} {language === 'tr' ? 'solar seri' : 'solar streak'}
              </span>
              <Flame className="w-5 h-5 text-orange-400" />
            </div>
          </motion.div>
        )}

        {/* Loop Close */}
        {output.loop_close && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-5 mb-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20" data-testid="output-loop-close">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-sans">
                {language === 'tr' ? 'Döngü Kapandı' : 'Loop Closed'}
              </span>
            </div>
            {morningOneWin && (
              <p className="text-[#6B7280] text-xs font-sans mb-2 italic">
                {language === 'tr' ? 'Hedef' : 'Target'}: "{morningOneWin}"
              </p>
            )}
            <p className="text-[#F8F5F2] font-sans text-sm leading-relaxed">{output.loop_close}</p>
          </motion.div>
        )}

        {/* Reflection */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="glass-panel rounded-2xl p-5 mb-4" data-testid="output-reflection">
          <div className="flex items-center gap-2 mb-3">
            <Moon className="w-4 h-4 text-purple-400" />
            <span className="text-xs uppercase tracking-wider text-purple-400 font-sans">
              {language === 'tr' ? 'Günün Yansıması' : "Day's Reflection"}
            </span>
          </div>
          <p className="text-[#D1D1D6] font-sans text-sm leading-relaxed">{output.reflection}</p>
        </motion.div>

        {/* Closing Mantra */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="rounded-2xl p-6 mb-6 bg-gradient-to-b from-white/5 to-transparent border border-white/10 text-center"
          data-testid="output-mantra">
          <Sparkles className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
          <p className="text-[#F8F5F2] font-serif text-xl italic leading-relaxed">"{output.closing_mantra}"</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <button onClick={() => navigate('/dashboard')}
            className="w-full py-4 rounded-2xl font-sans text-sm uppercase tracking-widest transition-all duration-300 bg-white/5 border border-white/10 text-[#A1A1AA] hover:bg-white/10 flex items-center justify-center gap-2"
            data-testid="return-to-dashboard-button">
            {language === 'tr' ? 'İyi Geceler' : 'Rest Well'}
            <Moon className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default EveningCheckIn;
